import { createAsyncThunk } from '@reduxjs/toolkit';

import { ExternalAppointment } from '../../types';
import CLIENT from '../../utils/CLIENT';
import { TimeHelper } from '../../utils';
import { convertExternalAppointment } from '../appointments/thunks';
import {
  CustomerSearchRow,
  GetSeriesByIdPayload,
  RecordEventOutcomePayload,
  SearchCustomersPayload,
  SearchSeriesPayload,
  SearchSeriesResponse,
  Series,
  SeriesIndexDocument,
  SeriesIndexDocumentRaw,
  SeriesIndexEvent,
  SeriesIndexEventRaw,
  SeriesWithEvents,
  UpdateSeriesPayload,
} from './types';

// Same convention as `convertExternalAppointment` in store/appointments/thunks:
// project the wire `dateTime` (epoch ms) into the legacy `{ date, time }` pair
// the timeline helpers consume, using the store's timezone.
function convertIndexEvent(event: SeriesIndexEventRaw, timezone: string): SeriesIndexEvent {
  const { date, time } = TimeHelper.timestampToDateAndTimeTz(event.dateTime, timezone);
  return {
    id: event.id,
    date,
    time,
    duration: event.duration,
    outcome: event.outcome,
    serviceIds: event.serviceIds,
  };
}

function convertIndexDoc(raw: SeriesIndexDocumentRaw, timezone: string): SeriesIndexDocument {
  const events: SeriesIndexEvent[] = [];
  for (let eventsIndex = 0; eventsIndex < raw.events.length; eventsIndex += 1) {
    events.push(convertIndexEvent(raw.events[eventsIndex], timezone));
  }
  return { ...raw, events };
}

export const searchCustomers = createAsyncThunk<CustomerSearchRow[], SearchCustomersPayload>(
  'series/searchCustomers',
  async ({ q }) => {
    const response = await CLIENT({
      url: `/customers/search?q=${encodeURIComponent(q)}`,
      method: 'GET',
    });

    return response.data as CustomerSearchRow[];
  },
);

export const getSeriesById = createAsyncThunk<SeriesWithEvents, GetSeriesByIdPayload>(
  'series/getSeriesById',
  async ({ id }, thunkAPI) => {
    const state = thunkAPI.getState() as any;
    const { timezone } = state.store.data;

    const response = await CLIENT({
      url: `/series/${encodeURIComponent(id)}`,
      method: 'GET',
    });

    const { series, events } = response.data as { series: Series; events: ExternalAppointment[] };

    return {
      series,
      events: events.map((event) => convertExternalAppointment(event, timezone)),
    };
  },
);

export const searchSeries = createAsyncThunk<SearchSeriesResponse, SearchSeriesPayload>(
  'series/searchSeries',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState() as any;
    const { timezone } = state.store.data;

    const params = new URLSearchParams();
    if (payload.q !== undefined) {
      params.set('q', payload.q);
    }
    if (payload.warnings !== undefined && payload.warnings.length > 0) {
      params.set('warnings', payload.warnings.join(','));
    }
    if (payload.sort !== undefined) {
      params.set('sort', payload.sort);
    }
    if (payload.archived !== undefined) {
      params.set('archived', String(payload.archived));
    }
    const query = params.toString();

    const response = await CLIENT({
      url: query ? `/series/search?${query}` : '/series/search',
      method: 'GET',
    });

    const body = response.data as {
      items: SeriesIndexDocumentRaw[];
      archivedCount: number;
    };

    const items: SeriesIndexDocument[] = [];
    for (let rowsIndex = 0; rowsIndex < body.items.length; rowsIndex += 1) {
      items.push(convertIndexDoc(body.items[rowsIndex], timezone));
    }

    return { items, archivedCount: body.archivedCount };
  },
);

export const updateSeries = createAsyncThunk<Series, UpdateSeriesPayload>(
  'series/updateSeries',
  async ({ id, ...body }) => {
    const response = await CLIENT({
      url: `/series/${encodeURIComponent(id)}`,
      method: 'PATCH',
      body,
    });

    return response.data as Series;
  },
);

export const recordSeriesOutcome = createAsyncThunk<Series, { id: string; outcome: number; eventId?: string }>(
  'series/recordSeriesOutcome',
  async ({ id, outcome, eventId }) => {
    const response = await CLIENT({
      url: `/series/${encodeURIComponent(id)}/outcome`,
      method: 'PATCH',
      body: { outcome, eventId },
    });
    return response.data as Series;
  },
);

// `outcome: null` is the undo signal — the backend resets the event (clearing
// its outcome, resurrecting it if it was cancelled) and reactivates the series
// if it had closed. See the parent API contract (LT-4074, per-appointment v1).
export const recordEventOutcome = createAsyncThunk<unknown, RecordEventOutcomePayload>(
  'series/recordEventOutcome',
  async ({ eventId, outcome }) => {
    const response = await CLIENT({
      url: `/calendar-events/${encodeURIComponent(eventId)}/outcome`,
      method: 'PATCH',
      body: { outcome },
    });
    return response.data;
  },
);
